import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, MessageCircle, FileText, X } from "lucide-react";

/**
 * BlogShell — reuses the exact Nav + Footer look from Landing.jsx so the
 * marketing site feels like one cohesive experience.
 * Children render inside a linen background.
 */
export default function BlogShell({ children }) {
  return (
    <div className="min-h-screen bg-linen text-ink font-sans">
      <BlogNav />
      {children}
      <BlogFooter />
      <BlogSupportFab />
    </div>
  );
}

function BlogNav() {
  return (
    <header
      className="sticky top-0 z-40 bg-linen/80 backdrop-blur-md border-b border-[#E2DCD0]"
      data-testid="blog-nav"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="blog-brand-link" className="flex items-center gap-2.5">
          <img
            src="/viyora-icon-64.png"
            alt="Viyora logo"
            className="w-8 h-8 rounded-lg"
            width="32"
            height="32"
          />
          <span className="font-serif text-xl tracking-tight text-ink">Viyora</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-sm text-ink/80">
          <Link to="/#features" className="hover:text-ink transition-colors">Features</Link>
          <Link to="/#pricing" className="hover:text-ink transition-colors">Pricing</Link>
          <Link to="/blog" className="text-ink font-medium">Blog</Link>
          <Link to="/#contact" className="hover:text-ink transition-colors">Contact</Link>
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="tel:+919810453892"
            data-testid="blog-nav-phone"
            aria-label="Call Viyora on +91 9810453892"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-ink/80 border border-pine/25 hover:border-pine rounded-full px-3 py-1.5 transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            +91 9810453892
          </a>
          <Link
            to="/login"
            data-testid="blog-nav-login"
            className="text-sm font-medium text-ink/90 hover:text-ink transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            data-testid="blog-nav-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen text-sm font-semibold px-5 py-2 hover:bg-pine/90 transition-colors"
          >
            Start trial <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function BlogFooter() {
  return (
    <footer className="bg-pine text-linen/70 py-16" data-testid="blog-footer">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/viyora-icon-64.png" alt="Viyora" className="w-8 h-8 " />
            <span className="font-serif text-xl text-linen">Viyora</span>
          </div>
          <p className="text-linen/60 leading-relaxed text-[13px]">
            Viyora is a cloud-based clinic management platform developed and
            operated by Viyora Tech.
          </p>
        </div>
        <div>
          <div className="text-linen font-medium mb-4 text-[13px] uppercase tracking-wider">Product</div>
          <ul className="space-y-2 text-[13px]">
            <li><Link to="/#features" className="hover:text-linen">Features</Link></li>
            <li><Link to="/#pricing" className="hover:text-linen">Pricing</Link></li>
            <li><Link to="/blog" className="hover:text-linen">Blog</Link></li>
            <li><Link to="/signup" className="hover:text-linen">Start free trial</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-linen font-medium mb-4 text-[13px] uppercase tracking-wider">Legal</div>
          <ul className="space-y-2 text-[13px]">
            <li><Link to="/legal/terms" className="hover:text-linen">Terms of service</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-linen">Privacy policy</Link></li>
            <li><Link to="/legal/dpdp" className="hover:text-linen">DPDP compliance</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-linen font-medium mb-4 text-[13px] uppercase tracking-wider">Contact</div>
          <ul className="space-y-2 text-[13px]">
            <li><a href="mailto:admin@viyora.co.in" className="hover:text-linen">admin@viyora.co.in</a></li>
            <li><a href="tel:+919810453892" className="hover:text-linen">+91 9810453892</a></li>
            <li>
              <a
                href="https://wa.me/919810453892?text=Hi%20Viyora%2C%20I%20have%20a%20question"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-linen inline-flex items-center gap-1.5"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-[#25D366]" />WhatsApp us
              </a>
            </li>
            <li>Noida, India</li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-linen/10 text-[12px] text-linen/50 flex flex-wrap items-center justify-between gap-4">
        <div>© 2026 Viyora Tech. All rights reserved.</div>
        <div>Made with care in India.</div>
      </div>
    </footer>
  );
}

function BlogSupportFab() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="fixed z-40 bottom-6 left-6" data-testid="blog-contact-widget">
      {open && (
        <div className="mb-3 w-72 rounded-2xl bg-white border border-[#E2DCD0] shadow-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-serif text-lg text-ink">Talk to us</div>
            <button
              onClick={() => setOpen(false)}
              className="text-ink-soft hover:text-ink"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <a
            href="https://wa.me/919810453892?text=Hi%20Viyora%2C%20I%20have%20a%20question"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 rounded-xl bg-[#25D366] text-white px-4 py-3 hover:bg-[#1DA850] mb-2"
          >
            <MessageCircle className="w-4 h-4" />
            <div>
              <div className="text-sm font-medium">WhatsApp us</div>
              <div className="text-[11px] opacity-90">+91 9810453892</div>
            </div>
          </a>
          <a
            href="tel:+919810453892"
            className="w-full flex items-center gap-3 rounded-xl bg-pine text-linen px-4 py-3 hover:bg-pine/90 mb-2"
          >
            <Phone className="w-4 h-4" />
            <div>
              <div className="text-sm font-medium">Call us</div>
              <div className="text-[11px] opacity-80">+91 9810453892</div>
            </div>
          </a>
          <a
            href="mailto:admin@viyora.co.in"
            className="w-full flex items-center gap-3 rounded-xl bg-linen text-ink border border-[#E2DCD0] px-4 py-3 hover:bg-white"
          >
            <FileText className="w-4 h-4" />
            <div>
              <div className="text-sm font-medium">Email</div>
              <div className="text-[11px] text-ink-mid">admin@viyora.co.in</div>
            </div>
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Contact support"
        data-testid="blog-contact-fab"
        className="w-14 h-14 rounded-full bg-pine text-linen shadow-lg hover:bg-pine/90 grid place-items-center transition hover:scale-105 active:scale-95"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    </div>
  );
}
